import 'antd/dist/antd.css';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import './App.scss';

import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';
import store from 'src/operations/store';
import queryString from 'query-string';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import App from './App';
import getClient from './apollo/client';
import { saveToken, saveExternalToken } from './utils';

import * as serviceWorker from './serviceWorker';

// Don't report events to Sentry while running on localhost (local development)
const isLocalhost =
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

Sentry.init({
  dsn: 'https://c7a70b6bed2f4b0a8b3922d14ef19b77@o408950.ingest.us.sentry.io/4511267895640064',
  integrations: [new BrowserTracing()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'NotAllowedError:',
    'AbortError:',
    'NotSupportedError:',
    'Permissions check failed',
  ],
  beforeSend(event) {
    // Drop all events originating from localhost
    if (isLocalhost) {
      return null;
    }

    const exceptionValue = event?.exception?.values?.[0]?.value || '';
    const exceptionType = event?.exception?.values?.[0]?.type || '';

    if (
      typeof exceptionValue === 'string' &&
      (exceptionValue.includes('Object captured as exception with keys: message') ||
        exceptionValue.includes('Permissions check failed'))
    ) {
      return null;
    }

    if (exceptionType === 'TypeError' && exceptionValue.includes('Permissions check failed')) {
      return null;
    }

    const eventMessage = event?.message || event?.logentry?.formatted || '';
    const requestUrl = event?.request?.url || '';
    const hasVimeoContext = [eventMessage, requestUrl].some((v) => typeof v === 'string' && /vimeo/i.test(v));
    if (
      event?.level === 'info' &&
      typeof eventMessage === 'string' &&
      eventMessage.includes('N+1 API Call') &&
      hasVimeoContext
    ) {
      return null;
    }

    return event;
  },
  beforeSendTransaction(event) {
    // Drop all performance transactions originating from localhost
    if (isLocalhost) {
      return null;
    }

    const spans = event?.spans || [];
    const vimeoSpanCount = spans.filter((span) => {
      const op = span?.op || '';
      const description = span?.description || '';
      return (
        (op === 'resource.fetch' || op === 'http.client') &&
        typeof description === 'string' &&
        /vimeo/i.test(description)
      );
    }).length;

    // Ignore Vimeo-only N+1-like trace noise.
    if (vimeoSpanCount >= 2) {
      return null;
    }

    return event;
  },
});

const params = queryString.parse(window.location.search);

if (params.token) {
  const token = params.token[0] === '"' ? JSON.stringify(JSON.parse(JSON.parse(params.token))) : params.token;
  saveToken(token);

  if (params.external_token) {
    saveExternalToken(params.external_token);
  } else {
    saveExternalToken(null);
  }
  const updatedPath = window.location.pathname;
  let newQuery = `${Object.keys(params)
    .filter((e) => e !== 'token' && e !== 'external_token')
    .map((key) => `${key}=${params[key]}`)
    .join('&')}`;
  if (newQuery !== '') {
    newQuery = `?${newQuery}`;
  }

  setTimeout(() => {
    window.location.href = `${updatedPath}${newQuery}`;
  }, 500);
} else {
  const client = getClient();

  ReactDOM.render(
    <ApolloProvider client={client}>
      <BrowserRouter>
        <Provider store={store}>
          <App />
        </Provider>
      </BrowserRouter>
    </ApolloProvider>,
    document.getElementById('root'),
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
